import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: any | null;
  loading: boolean;
  signUp: (email: string, password: string, userData?: any) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updateProfile: (updates: any) => Promise<{ error: any }>;
  
  // Computed properties for backward compatibility
  isAuthenticated: boolean;
  isLoading: boolean;
  logoutUser: () => Promise<{ error: any }>;
  role: string | undefined;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  loading: true,
  signUp: async () => ({ error: null }),
  signIn: async () => ({ error: null }),
  signOut: async () => ({ error: null }),
  resetPassword: async () => ({ error: null }),
  updateProfile: async () => ({ error: null }),
  isAuthenticated: false,
  isLoading: true,
  logoutUser: async () => ({ error: null }),
  role: undefined,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile from our profiles table
  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }

      // Authoritative role comes from user_roles, not profiles.role
      const { data: roleData } = await supabase.rpc('get_primary_role', { _user_id: userId });
      return { ...data, role: (roleData as string) || data?.role || 'user' };
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };


  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Fetch profile when user signs in
        if (session?.user) {
          setTimeout(async () => {
            const userProfile = await fetchUserProfile(session.user.id);
            setProfile(userProfile);
          }, 0);
        } else {
          setProfile(null);
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserProfile(session.user.id).then(setProfile);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, userData?: any) => {
    try {
      const redirectUrl = `${window.location.origin}/`;

      // SECURITY: never forward a client-supplied `role` — it is ignored by
      // the DB trigger, but we strip it here as defense in depth.
      const safeUserData = userData ? { ...userData } : undefined;
      if (safeUserData && 'role' in safeUserData) delete safeUserData.role;

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: safeUserData
        }
      });

      return { error };
    } catch (error) {
      return { error };
    }
  };


  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      return { error };
    } catch (error) {
      return { error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      // Clear local state
      setUser(null);
      setSession(null);
      setProfile(null);

      return { error };
    } catch (error) {
      return { error };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const redirectUrl = `${window.location.origin}/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl
      });

      return { error };
    } catch (error) {
      return { error };
    }
  };

  const updateProfile = async (updates: any) => {
    try {
      if (!user) return { error: 'No user logged in' };

      // SECURITY: role changes must go through admin flows, never self-update
      const safeUpdates = { ...updates };
      if ('role' in safeUpdates) delete safeUpdates.role;

      const { error } = await supabase
        .from('profiles')
        .update(safeUpdates)
        .eq('id', user.id);


      if (!error) {
        // Refresh profile data
        const updatedProfile = await fetchUserProfile(user.id);
        setProfile(updatedProfile);
      }

      return { error };
    } catch (error) {
      return { error };
    }
  };

  const contextValue: AuthContextType = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
    // Computed properties for backward compatibility
    isAuthenticated: !!user,
    isLoading: loading,
    logoutUser: signOut,
    role: profile?.role,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Helper functions for role checking
export const isAdmin = (profile: any): boolean => {
  return profile?.role === 'admin' || profile?.role === 'superadmin' || profile?.role === 'masterAdmin';
};

export const isSuperAdmin = (profile: any): boolean => {
  return profile?.role === 'superadmin' || profile?.role === 'masterAdmin';
};

export const isMasterAdmin = (profile: any): boolean => {
  return profile?.role === 'masterAdmin';
};